
// export default MyEnrolledCourses;

import { useDebugValue, useEffect, useState } from "react";
import { getMyEnrolledCourses } from "../api/EnrollmentApis";
import { getCourseProgressSummary } from "../api/LessonProgressApi";
import "../static/EnrolledCourses.css";
import { Link } from "react-router-dom";
import NotEnrolledCourses from "./NotEnrolledCourses";
import Footer from "../components/common/Footer";
import LoadingState from "../components/common/LoadingState";

function MyEnrolledCourses() {
  const [myCourses, setMyCourses] = useState(null);
  const [myProjects, setMyProjects] = useState(null);
  const [courseProgress, setCourseProgress] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchAllCourseProgress = async (courses) => {
      try {
        const promises = courses.map((course) =>
          getCourseProgressSummary(course.slug)                     // <-- was course.id
            .then((res) => ({ courseSlug: course.slug, progress: res.data }))   // <-- slug key
            .catch(() => ({ courseSlug: course.slug, progress: null }))
        );
        const results = await Promise.all(promises);
        return results.reduce((acc, r) => {
          if (r.progress) acc[r.courseSlug] = r.progress;           // <-- slug key
          return acc;
        }, {});
      } catch {
        return {};
      }
    };

    const fetchAll = async () => {
      try {
        const [coursesRes, projectsRes] = await Promise.all([
          getMyEnrolledCourses({ is_project: false }),
          getMyEnrolledCourses({ is_project: true }),
        ]);

        const coursesData = coursesRes.data;
        const projectsData = projectsRes.data;

        if (coursesData?.user?.id) setCurrentUserId(coursesData.user.id);
        else if (projectsData?.user?.id) setCurrentUserId(projectsData.user.id);
        else if (coursesData?.user_id) setCurrentUserId(coursesData.user_id);

        setMyCourses(coursesData);
        setMyProjects(projectsData);

        const allItems = [
          ...(coursesData?.enrolled_courses || []),
          ...(projectsData?.enrolled_courses || []),
        ];

        if (allItems.length > 0) {
          const progressMap = await fetchAllCourseProgress(allItems);
          setCourseProgress(progressMap);
        }
      } catch {
        setError("Unable to load your courses. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  useDebugValue(myCourses ?? "Loading...");

  // Keyed by slug now
  const getCourseProgressData = (slug) =>                     // <-- was id
    courseProgress[slug] || {                                 // <-- slug lookup
      progress_percentage: 0,
      completed_lessons: 0,
      total_lessons: 0,
      total_watched_seconds: 0,
      last_accessed: null,
      enrollment_progress: 0,
    };

  const getProgressVariant = (p) => {
    if (p === 0) return "ec-progress-fill--not-started";
    if (p === 100) return "ec-progress-fill--complete";
    return "ec-progress-fill--in-progress";
  };

  const getProgressText = (p, c, t) => {
    if (p === 0) return "Not started";
    if (p === 100) return "Completed";
    return `${c}/${t} lessons (${p}%)`;
  };

  const getProgressTextClass = (p) => {
    if (p === 100) return "ec-progress-text--complete";
    if (p === 0) return "ec-progress-text--not-started";
    return "ec-progress-text--in-progress";
  };

  const formatTimeSpent = (sec) => {
    if (!sec) return "No time spent";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m spent` : `${m}m spent`;
  };

  const getLastAccessedText = (last) => {
    if (!last) return "Not started";
    const date = new Date(last);
    const now = new Date();
    const diffMs = Math.abs(now - date);
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return "Last accessed today";
    if (days === 1) return "Last accessed yesterday";
    if (days < 7) return `Last accessed ${days} days ago`;
    if (days < 30) return `Last accessed ${Math.floor(days / 7)} weeks ago`;
    return `Last accessed ${Math.floor(days / 30)} months ago`;
  };

  const getEnrollmentDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return `Enrolled on ${date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;
  };

  const filterItems = (list) =>
    (list || []).filter(
      (c) =>
        c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const filteredCourses = filterItems(myCourses?.enrolled_courses);
  const filteredProjects = filterItems(myProjects?.enrolled_courses);

  const renderGrid = (items, emptyLabel) => {
    if (items.length === 0) {
      return (
        <div className="ec-empty">
          <div className="ec-empty-icon">📚</div>
          <h3 className="ec-empty-title">{emptyLabel}</h3>
        </div>
      );
    }

    return (
      <div className="ec-grid">
        {items.map((course) => {
          // If the API forgot to send `slug` for an enrolled course, fall back
          // to id so we never build /content/undefined — but this should not
          // happen once the serializer exposes slug.
          const slugOrId = course.slug || course.id;
          const p = getCourseProgressData(slugOrId);            // <-- was course.id
          const progress = Math.round(p.progress_percentage || 0);

          return (
            <Link
              to={`/content/${slugOrId}`}                       // <-- was course.id
              key={course.id}
              className="ec-card-link"
            >
              <div className="ec-card">
                <div className="ec-card-header">
                  <h3 className="ec-course-title">{course.title}</h3>
                  <div className="ec-course-meta">
                    {course.instructor_name && (
                      <span className="ec-instructor">By {course.instructor_name}</span>
                    )}
                    {course.category && (
                      <span className="ec-category">{course.category}</span>
                    )}
                  </div>
                </div>

                <div className="ec-progress-section">
                  <div className="ec-progress-header">
                    <span className="ec-progress-label">Your Progress</span>
                    <span className="ec-progress-percentage">{progress}%</span>
                  </div>

                  <div className="ec-progress-bar">
                    <div
                      className={`ec-progress-fill ${getProgressVariant(progress)}`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>

                  <div className="ec-progress-details">
                    <p className={`ec-progress-text ${getProgressTextClass(progress)}`}>
                      {getProgressText(
                        progress,
                        p.completed_lessons,
                        p.total_lessons || course.total_lessons
                      )}
                    </p>

                    {p.last_accessed && (
                      <p className="ec-last-accessed">
                        {getLastAccessedText(p.last_accessed)}
                      </p>
                    )}

                    {p.total_watched_seconds > 0 && (
                      <p className="ec-time-spent">
                        {formatTimeSpent(p.total_watched_seconds)}
                      </p>
                    )}

                    {course.enrolled_at && (
                      <p className="ec-enrollment-date">
                        {getEnrollmentDate(course.enrolled_at)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <LoadingState
        variant="page"
        message="Loading your courses and progress..."
      />
    );
  }

  if (error) {
    return (
      <div className="ec-page">
        <div className="ec-courses">
          <div className="ec-error">
            <div className="ec-error-icon">⚠️</div>
            <h3 className="ec-error-title">Something went wrong</h3>
            <p className="ec-error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const courseCount = myCourses?.enrolled_courses?.length || 0;
  const projectCount = myProjects?.enrolled_courses?.length || 0;

  return (
    <div className="ec-page">
      <div className="ec-container">
        <div className="ec-courses">
          <div className="ec-header">
            <h1 className="ec-title">My Learning</h1>
            <p className="ec-subtitle">
              {courseCount} course{courseCount !== 1 ? "s" : ""} ·{" "}
              {projectCount} project{projectCount !== 1 ? "s" : ""}
            </p>
          </div>

          {(courseCount > 0 || projectCount > 0) && (
            <div className="ec-search-container">
              <input
                type="text"
                placeholder={isSmallScreen ? "Search..." : "Search your courses and projects"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ec-search"
              />
            </div>
          )}

          <section className="ec-section">
            <h2 className="ec-section-title">My Courses</h2>
            {renderGrid(filteredCourses, "No courses enrolled yet")}
          </section>

          <section className="ec-section">
            <h2 className="ec-section-title">My Projects</h2>
            {renderGrid(filteredProjects, "No projects enrolled yet")}
          </section>
        </div>

        {currentUserId && <NotEnrolledCourses userId={currentUserId} />}
      </div>

      <Footer />
    </div>
  );
}

export default MyEnrolledCourses;